/**
 * Modal where a super_admin assigns None / Read / Write per item on a specific admin.
 * Loads the catalog + current grants on open; saves a replacement map on submit.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, Modal, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

type Level = 'read' | 'write';
type Choice = 'none' | Level;

interface PermissionItem {
  label: string;
  readDesc: string;
  writeDesc?: string;
  levels: Level[];
}

interface PermissionGroup {
  label: string;
  keys: string[];
}

interface CatalogResponse {
  groups: PermissionGroup[];
  items: Record<string, PermissionItem>;
}

interface MemberResponse {
  isSuperAdmin: boolean;
  levels: Record<string, Level>;
}

interface Props {
  readonly visible: boolean;
  readonly adminId: string;
  readonly adminName: string;
  readonly adminEmail: string;
  readonly adminRole: string;
  readonly onClose: () => void;
  readonly onSaved?: () => void;
}

export default function AdminPermissionsModal({ visible, adminId, adminName, adminEmail, adminRole, onClose, onSaved }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [granted, setGranted] = useState<Record<string, Level>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const isTargetSuperAdmin = adminRole === 'super_admin';

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    setError(null);
    try {
      const [cat, mem] = await Promise.all([
        api<CatalogResponse>('/api/admin/permissions/catalog'),
        api<MemberResponse>(`/api/admin/admins/${adminId}/permissions`),
      ]);
      setCatalog(cat);
      setGranted(mem.levels || {});
    } catch (e: any) {
      setError(e?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [visible, adminId]);

  useEffect(() => { load(); }, [load]);

  const setChoice = (key: string, choice: Choice) => {
    setGranted(prev => {
      const next = { ...prev };
      if (choice === 'none') delete next[key];
      else next[key] = choice;
      return next;
    });
  };

  const setGroupAll = (group: PermissionGroup, choice: Choice) => {
    if (!catalog) return;
    setGranted(prev => {
      const next = { ...prev };
      for (const k of group.keys) {
        const item = catalog.items[k];
        if (!item) continue;
        if (choice === 'none') {
          delete next[k];
        } else if (item.levels.includes(choice)) {
          next[k] = choice;
        } else {
          // fall back to the highest available (e.g., write requested but item is read-only)
          next[k] = item.levels[item.levels.length - 1];
        }
      }
      return next;
    });
  };

  const toggleCollapsed = (label: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const counts = useMemo(() => {
    if (!catalog) return { none: 0, read: 0, write: 0, total: 0 };
    let n = 0, r = 0, w = 0, total = 0;
    for (const grp of catalog.groups) {
      total += grp.keys.length;
      for (const k of grp.keys) {
        const lvl = granted[k];
        if (!lvl) n++;
        else if (lvl === 'read') r++;
        else w++;
      }
    }
    return { none: n, read: r, write: w, total };
  }, [catalog, granted]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api(`/api/admin/admins/${adminId}/permissions`, {
        method: 'PUT',
        body: { levels: granted },
      });
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderChoice = (key: string, item: PermissionItem) => {
    const current: Choice = granted[key] ?? 'none';
    const options: { value: Choice; label: string; available: boolean }[] = [
      { value: 'none', label: 'None', available: true },
      { value: 'read', label: 'Read', available: item.levels.includes('read') },
      { value: 'write', label: 'Write', available: item.levels.includes('write') },
    ];
    return (
      <View style={styles.choiceRow}>
        {options.map(opt => {
          const active = current === opt.value;
          const disabled = !opt.available;
          return (
            <TouchableOpacity
              key={opt.value}
              disabled={disabled}
              onPress={() => setChoice(key, opt.value)}
              style={[
                styles.choice,
                active && opt.value === 'none' && styles.choiceActiveNone,
                active && opt.value === 'read' && styles.choiceActiveRead,
                active && opt.value === 'write' && styles.choiceActiveWrite,
                disabled && styles.choiceDisabled,
              ]}
            >
              <Text style={[
                styles.choiceText,
                active && styles.choiceTextActive,
                disabled && styles.choiceTextDisabled,
              ]}>{opt.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.content, { paddingBottom: modalBottomPadding }]}>
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Permissions</Text>
              <Text style={styles.subtitle}>{adminName} · {adminEmail}</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {isTargetSuperAdmin ? (
            <View style={styles.banner}>
              <Ionicons name="shield-checkmark" size={20} color={colors.primary} />
              <Text style={styles.bannerText}>This admin is a super_admin and has full access. Levels below are stored but not consulted.</Text>
            </View>
          ) : null}

          {loading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginVertical: spacing.xl }} />
          ) : !catalog ? (
            <Text style={styles.error}>{error || 'No catalog available'}</Text>
          ) : (
            <>
              <Text style={styles.summary}>
                {counts.write} write · {counts.read} read · {counts.none} none · {counts.total} total
              </Text>
              <ScrollView style={styles.scroll} contentContainerStyle={{ paddingBottom: spacing.md }} nestedScrollEnabled>
                {catalog.groups.map(group => {
                  const isCollapsed = collapsed.has(group.label);
                  return (
                    <View key={group.label} style={styles.group}>
                      <View style={styles.groupHeader}>
                        <TouchableOpacity style={styles.groupTitleBtn} onPress={() => toggleCollapsed(group.label)}>
                          <Ionicons name={isCollapsed ? 'chevron-forward' : 'chevron-down'} size={14} color={colors.textSecondary} />
                          <Text style={styles.groupTitle}>{group.label}</Text>
                        </TouchableOpacity>
                        <View style={styles.bulkRow}>
                          <TouchableOpacity onPress={() => setGroupAll(group, 'none')} style={styles.bulkBtn}>
                            <Text style={[styles.bulkText, { color: colors.textSecondary }]}>None</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => setGroupAll(group, 'read')} style={styles.bulkBtn}>
                            <Text style={[styles.bulkText, { color: colors.primary }]}>Read</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => setGroupAll(group, 'write')} style={styles.bulkBtn}>
                            <Text style={[styles.bulkText, { color: '#059669' }]}>Write</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                      {!isCollapsed && group.keys.map(k => {
                        const item = catalog.items[k];
                        if (!item) return null;
                        const desc = granted[k] === 'write' && item.writeDesc ? item.writeDesc : item.readDesc;
                        return (
                          <View key={k} style={styles.itemRow}>
                            <View style={{ flex: 1, paddingRight: spacing.sm }}>
                              <Text style={styles.itemLabel}>{item.label}</Text>
                              <Text style={styles.itemKey}>{k}</Text>
                              <Text style={styles.itemDesc}>{desc}</Text>
                            </View>
                            {renderChoice(k, item)}
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
              </ScrollView>
            </>
          )}

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving || loading}>
              <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  content: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 760, maxHeight: '92%', elevation: 5 },
  header: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: spacing.md },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  banner: { flexDirection: 'row', gap: spacing.sm, backgroundColor: c.primary + '15', padding: spacing.sm, borderRadius: borderRadius.md, marginBottom: spacing.md, alignItems: 'center' },
  bannerText: { color: c.text, fontSize: fontSize.xs, flex: 1 },
  summary: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  scroll: { maxHeight: 520 },
  group: { borderTopWidth: 1, borderTopColor: c.border, paddingTop: spacing.sm, marginBottom: spacing.sm },
  groupHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  groupTitleBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
  groupTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  bulkRow: { flexDirection: 'row', gap: spacing.xs },
  bulkBtn: { paddingHorizontal: spacing.xs, paddingVertical: 2 },
  bulkText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  itemRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.xs },
  itemLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  itemKey: { fontSize: 10, color: c.textLight, fontFamily: 'monospace', marginTop: 1 },
  itemDesc: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  choiceRow: { flexDirection: 'row', gap: 4 },
  choice: { paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.border, backgroundColor: c.background, minWidth: 52, alignItems: 'center' },
  choiceActiveNone: { backgroundColor: c.textLight + '30', borderColor: c.textLight },
  choiceActiveRead: { backgroundColor: c.primary, borderColor: c.primary },
  choiceActiveWrite: { backgroundColor: '#059669', borderColor: '#059669' },
  choiceDisabled: { opacity: 0.35 },
  choiceText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.textSecondary },
  choiceTextActive: { color: c.white },
  choiceTextDisabled: { color: c.textLight },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md },
  cancelText: { color: c.text, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, backgroundColor: c.primary, borderRadius: borderRadius.md },
  saveText: { color: c.white, fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  error: { color: c.danger, fontSize: fontSize.xs, marginTop: spacing.sm },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
